import { mapChapterIdForExport } from "./openAcrReportShared";

function asString(value) {
  if (value == null) return "";
  return String(value);
}

function mapComponentNameForExport(name) {
  const raw = asString(name).trim();
  if (!raw) return "none";
  if (raw === "Web") return "web";
  if (raw === "ElectronicDocs") return "electronic-docs";
  if (raw === "Software") return "software";
  if (raw === "AuthoringTool") return "authoring-tool";
  return raw.toLowerCase();
}

function normalizeConformanceLevel(value) {
  const raw = asString(value).trim();
  if (!raw) return "";
  if (raw === "Supports") return "supports";
  if (raw === "PartiallySupports") return "partially-supports";
  if (raw === "DoesNotSupport") return "does-not-support";
  if (raw === "NotApplicable") return "not-applicable";
  if (raw === "NotEvaluated") return "not-evaluated";
  return "";
}

function buildComponents(catalogCriterion, evaluationCriterion) {
  const evaluationComponents = Array.isArray(evaluationCriterion?.components)
    ? evaluationCriterion.components
    : [];
  const catalogComponents = Array.isArray(catalogCriterion?.components)
    ? catalogCriterion.components
    : [];

  const source = evaluationComponents.length > 0
    ? evaluationComponents
    : catalogComponents;

  if (!source.length) {
    return [{ name: "none", adherence: { level: "", notes: "" } }];
  }

  return source.map((component) => {
    const componentName = typeof component === "string" ? component : component?.name;
    return {
      name: mapComponentNameForExport(componentName),
      adherence: {
        level: normalizeConformanceLevel(component?.adherence?.level),
        notes: asString(component?.adherence?.notes),
      },
    };
  });
}

function buildChapters(catalog, evaluation) {
  const chapters = {};
  const catalogChapters = Array.isArray(catalog?.chapters) ? catalog.chapters : [];
  const evaluationChapters = evaluation?.chapters ?? {};

  catalogChapters.forEach((catalogChapter) => {
    const chapterId = mapChapterIdForExport(asString(catalogChapter?.id));
    if (!chapterId) return;
    const sourceChapterId = asString(catalogChapter?.id);
    const evalChapter = evaluationChapters?.[sourceChapterId] ?? {};
    const catalogCriteria = Array.isArray(catalogChapter?.criteria) ? catalogChapter.criteria : [];
    const evalCriteria = Array.isArray(evalChapter?.criteria) ? evalChapter.criteria : [];

    chapters[chapterId] = {
      notes: asString(evalChapter?.notes),
      disabled: Boolean(evalChapter?.disabled),
      criteria: catalogCriteria.map((catalogCriterion) => {
        const criterionNum = asString(catalogCriterion?.id ?? "");
        const evalCriterion = evalCriteria.find(
          (criterion) => asString(criterion?.num) === criterionNum
        );
        return {
          num: criterionNum,
          components: buildComponents(catalogCriterion, evalCriterion),
        };
      }),
    };
  });

  return chapters;
}

export default function buildAcrYamlModel(evaluation, catalog) {
  const product = evaluation?.product ?? {};
  const catalogName = asString(catalog?.name || catalog?.title || catalog?.label);
  return {
    title: asString(evaluation?.title),
    product: {
      name: asString(product?.name),
      version: asString(product?.version),
      description: asString(product?.description),
    },
    author: {
      name: asString(evaluation?.author?.name),
      company_name: asString(evaluation?.author?.company_name),
      address: asString(evaluation?.author?.address),
      email: asString(evaluation?.author?.email),
      phone: asString(evaluation?.author?.phone),
      website: asString(evaluation?.author?.website),
    },
    vendor: {
      name: asString(evaluation?.vendor?.name),
      company_name: asString(evaluation?.vendor?.company_name),
      address: asString(evaluation?.vendor?.address),
      email: asString(evaluation?.vendor?.email),
      phone: asString(evaluation?.vendor?.phone),
      website: asString(evaluation?.vendor?.website),
    },
    report_date: asString(evaluation?.report_date),
    last_modified_date: asString(evaluation?.last_modified_date),
    version: asString(evaluation?.version),
    notes: asString(evaluation?.notes),
    evaluation_methods_used: asString(evaluation?.evaluation_methods_used),
    legal_disclaimer: asString(evaluation?.legal_disclaimer),
    repository: asString(evaluation?.repository),
    feedback: asString(evaluation?.feedback),
    license: asString(evaluation?.license),
    related_acrs: Array.isArray(evaluation?.related_acrs)
      ? evaluation.related_acrs.map((item) => ({
        url: asString(item?.url),
        type: asString(item?.type) || "primary",
      }))
      : [],
    catalog: catalogName,
    chapters: buildChapters(catalog, evaluation),
  };
}
